import React, { useCallback, useMemo, useState } from 'react';
import {
	TokenGroup,
	SpaceBetween,
	Box,
	Input,
	Button,
	FormField,
} from '@amzn/awsui-components-console';
import { WatcherInputProps } from './watcherInput.interface';

// Accepts alias (e.g. "alice", "snow-team") or email (e.g. "snow-team@amazon.com", "alice@amazon.lu")
const ALIAS_REGEX = /^[a-zA-Z][a-zA-Z-]*$/;
const AMAZON_EMAIL_REGEX = /^[a-zA-Z][a-zA-Z.-]*@amazon\.[a-zA-Z]{2,}$/i;

const isValidAliasInput = (input: string): boolean =>
	ALIAS_REGEX.test(input) || AMAZON_EMAIL_REGEX.test(input);

const normalizeAlias = (input: string): string => {
	if (AMAZON_EMAIL_REGEX.test(input)) return input;
	return `${input}@amazon.com`;
};

// TODO: Integrate with people-finder API for alias autosuggest options
export default function WatcherInput({
	watchers,
	onAddWatcher,
	onRemoveWatcher,
	currentUserAlias,
	label = 'Watchers - optional',
	description = 'Add Amazon aliases to notify on workflow start, task failure, and workflow completion.',
	placeholder = 'Enter alias or Amazon email',
	dataTestId = 'watcher-input',
}: WatcherInputProps) {
	const [inputValue, setInputValue] = useState('');
	const [errorText, setErrorText] = useState('');

	const normalizedCurrentUser = useMemo(
		() => currentUserAlias ? normalizeAlias(currentUserAlias) : '',
		[currentUserAlias]
	);

	const isCurrentUserWatching = useMemo(
		() => normalizedCurrentUser
			? watchers.some(w => w.toLowerCase() === normalizedCurrentUser.toLowerCase())
			: false,
		[watchers, normalizedCurrentUser]
	);

	const handleAddWatcher = (alias: string) => {
		const trimmed = alias.trim();
		if (!trimmed) {
			setErrorText('Enter a valid alias. Just "@amazon.com" is not a valid input.');
			return;
		}

		if (!isValidAliasInput(trimmed)) {
			setErrorText('Enter a valid alias or Amazon email');
			return;
		}

		const normalized = normalizeAlias(trimmed);

		if (watchers.some(w => w.toLowerCase() === normalized.toLowerCase())) {
			setErrorText(`${normalized} is already added as a watcher`);
			return;
		}

		setErrorText('');
		onAddWatcher(normalized);
		setInputValue('');
	};

	const handleAddOrRemoveMe = useCallback(() => {
		if (!currentUserAlias) return;
		if (isCurrentUserWatching) {
			onRemoveWatcher(normalizedCurrentUser);
		} else {
			handleAddWatcher(currentUserAlias);
		}
	}, [currentUserAlias, isCurrentUserWatching, normalizedCurrentUser, onRemoveWatcher]);

	const tokenItems = useMemo(
		() => watchers.map(alias => ({
			label: alias.replace(/@amazon\.com$/i, ''),
			dismissLabel: `Remove ${alias}`,
		})),
		[watchers]
	);

	return (
		<FormField
			label={label}
			description={description}
			errorText={errorText}
			data-testid={dataTestId}
		>
			<Box>
				<SpaceBetween size="xs">
					<Input
						onChange={({ detail }) => {
							setInputValue(detail.value);
							if (errorText) setErrorText('');
						}}
						value={inputValue}
						type="text"
						placeholder={placeholder}
						invalid={!!errorText}
						data-testid={`${dataTestId}-input`}
						onKeyDown={(event) => {
							if (event.detail.key === 'Enter' && inputValue.trim()) {
								handleAddWatcher(inputValue);
							}
						}}
					/>
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
						<Button
							onClick={() => {
								handleAddWatcher(inputValue);
							}}
							disabled={!inputValue.trim()}
							data-testid={`${dataTestId}-add-button`}
						>
							Add to List
						</Button>
						{currentUserAlias && (
							<Button
								variant={isCurrentUserWatching ? 'normal' : 'primary'}
								onClick={handleAddOrRemoveMe}
								data-testid={`${dataTestId}-add-me-button`}
							>
								{isCurrentUserWatching ? 'Remove me' : 'Add me'}
							</Button>
						)}
						</SpaceBetween>
					</Box>
				</SpaceBetween>
				{watchers.length > 0 && (
					<TokenGroup
						items={tokenItems}
						onDismiss={({ detail: { itemIndex } }) => onRemoveWatcher(watchers[itemIndex])}
						data-testid={`${dataTestId}-token-group`}
					/>
				)}
			</Box>
		</FormField>
	);
}
